import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import AdminService from "../../../api/services/adminService";
import { clearAuth } from "../../../utils/authStorage";
import { formatWalletType } from "../../../utils/walletTypes";
import {
  LIST_LIMIT,
  LIST_LIMIT_OPTIONS,
  SEQUENCE_FALLBACK,
  STATUS_CHIPS,
  copyText,
  formatMoney,
  isReleaseJobRunning,
  isUnauthorized,
  userDisplayName,
} from "./roundReleaseLedgerUtils";
import "../Home/Homepage.css";
import "../Trading/AllOpenOrdersPage.css";
import "../Trading/CurrencyPairManagementPage.css";
import "../Staking/StakingStakesPage.css";
import "./RoundReleaseLedgerPage.css";

const EMPTY_PAGINATION = {
  page: 1,
  limit: LIST_LIMIT,
  total: 0,
  total_pages: 1,
  has_next: false,
  has_prev: false,
};

const RoundReleaseLedgerPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(LIST_LIMIT);
  const [pagination, setPagination] = useState(EMPTY_PAGINATION);
  const [sequence, setSequence] = useState(null);

  const [userSearch, setUserSearch] = useState("");
  const [debouncedUser, setDebouncedUser] = useState("");
  const [statusFilter, setStatusFilter] = useState("vesting");
  const [jobRunning, setJobRunning] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedUser(userSearch.trim()), 700);
    return () => clearTimeout(t);
  }, [userSearch]);

  useEffect(() => {
    setPage(1);
  }, [debouncedUser, statusFilter, pageLimit]);

  const hasFilters = Boolean(debouncedUser) || statusFilter !== "all";

  const buildParams = useCallback(
    (pageNum = page, limitNum = pageLimit) => {
      const params = { page: pageNum, limit: limitNum };
      if (debouncedUser) params.user = debouncedUser;
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      return params;
    },
    [page, pageLimit, debouncedUser, statusFilter]
  );

  const fetchRows = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) {
        setLoading(true);
        setError("");
      }
      try {
        const res = await AdminService.getReferralReleaseUsers(buildParams());
        if (isUnauthorized(res)) {
          clearAuth();
          window.location.href = "/";
          return;
        }
        const list = Array.isArray(res?.data) ? res.data : [];
        setRows(list);
        setSequence(res?.sequence || null);
        const pag = res?.pagination || {};
        setPagination({
          page: pag.page || page,
          limit: pag.limit || pageLimit,
          total: typeof pag.total === "number" ? pag.total : list.length,
          total_pages: Math.max(1, Number(pag.total_pages) || 1),
          has_next: typeof pag.has_next === "boolean" ? pag.has_next : false,
          has_prev: typeof pag.has_prev === "boolean" ? pag.has_prev : false,
        });
        if (res?.success === false && !silent) {
          setError(res?.message || "Failed to fetch round release ledger");
        }
      } catch (err) {
        if (!silent) {
          setRows([]);
          setError(err?.message || "Failed to fetch round release ledger");
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [buildParams, page, pageLimit]
  );

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    let cancelled = false;
    let timer = 0;
    let sawRunning = false;

    const tick = async () => {
      if (cancelled) return;
      try {
        const res = await AdminService.getReferralReleaseRoundNowStatus();
        if (cancelled) return;
        if (isUnauthorized(res)) return;
        const running = isReleaseJobRunning(res);
        setJobRunning(running);
        if (running) {
          sawRunning = true;
          timer = window.setTimeout(tick, 4000);
          return;
        }
        if (sawRunning) {
          sawRunning = false;
          await fetchRows({ silent: true });
        }
        timer = window.setTimeout(tick, 8000);
      } catch {
        if (!cancelled) timer = window.setTimeout(tick, 8000);
      }
    };

    tick();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [fetchRows]);

  const handleReset = () => {
    setUserSearch("");
    setDebouncedUser("");
    setStatusFilter("all");
    setPage(1);
  };

  const handleCopyUuid = async (e, uuid) => {
    e.stopPropagation();
    const ok = await copyText(uuid);
    if (ok) toast.success("UUID copied");
    else toast.error("Could not copy UUID");
  };

  const openDetail = (userId) => {
    if (!userId) return;
    navigate(`/dashboard/referral/round-releases/${userId}`);
  };

  const pageStart = rows.length === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const pageEnd = rows.length === 0 ? 0 : pageStart + rows.length - 1;

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Referral</span>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Round Release Ledger</span>
          </p>
          <h1 className="dashboard-hero__title">Round Release Ledger</h1>
          <p className="dashboard-hero__subtitle">
            Per-user total reward split by plan % into the staking wallet
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-book" />
        </div>
      </section>

      <div className="dashboard-body">
        <p className="rrl-note" role="note">
          <i className="fas fa-info-circle" /> {SEQUENCE_FALLBACK}
          {sequence?.rule && sequence.rule !== SEQUENCE_FALLBACK ? (
            <span> {sequence.rule}</span>
          ) : null}
          {sequence?.destination ? (
            <span className="rrl-note__dest"> {sequence.destination}</span>
          ) : null}
        </p>

        {jobRunning ? (
          <p className="rrl-processing" role="status">
            <i className="fas fa-spinner fa-spin" /> Round release is running. This list refreshes when the job finishes.
          </p>
        ) : null}

        <section className="openorders-section">
          <div className="openorders-section__header">
            <div className="openorders-section__title-wrap">
              <span className="openorders-section__icon">
                <i className="fas fa-users" />
              </span>
              <h2 className="openorders-section__title">Users</h2>
            </div>
            <div className="openorders-section__stats staking-stakes__header-actions">
              <span className="openorders-stat">
                <strong>{rows.length}</strong> on page
                {pagination.total != null && (
                  <>
                    {" "}
                    · <strong>{pagination.total}</strong> total
                  </>
                )}
              </span>
            </div>
          </div>

          <div className="rrl-filters">
            <input
              type="search"
              placeholder="Email, UUID, phone"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="openorders-search-input rrl-filters__search"
            />
            <div className="rrl-chips" role="tablist" aria-label="Vesting status">
              {STATUS_CHIPS.map((chip) => (
                <button
                  key={chip.value}
                  type="button"
                  className={`rrl-chip ${statusFilter === chip.value ? "rrl-chip--active" : ""}`}
                  onClick={() => setStatusFilter(chip.value)}
                >
                  {chip.label}
                </button>
              ))}
            </div>
            <button type="button" className="cp-btn cp-btn--outline" onClick={handleReset}>
              Reset
            </button>
          </div>

          {loading ? (
            <div className="openorders-empty">
              <i className="fas fa-spinner fa-spin" /> Loading…
            </div>
          ) : error ? (
            <div className="rrl-empty rrl-empty--error">
              <p>{error}</p>
              <button type="button" className="cp-btn cp-btn--primary" onClick={fetchRows}>
                Retry
              </button>
            </div>
          ) : rows.length > 0 ? (
            <>
              <div className="openorders-table-wrap">
                <table className="openorders-table rrl-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>UUID</th>
                      <th>Status</th>
                      <th>Total reward</th>
                      <th>Released</th>
                      <th>Remaining</th>
                      <th>Last round paid</th>
                      <th>Next round</th>
                      <th>Staking wallet</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.flatMap((row) => {
                      const user = row.user || {};
                      const userId = user.id || user._id;
                      const wallets = Array.isArray(row.staking_wallet) ? row.staking_wallet : [];
                      const lines = wallets.length ? wallets : [null];
                      const span = lines.length;
                      const name = userDisplayName(user);
                      const ccy = row.next_round_currency || wallets[0]?.currency || "USDT";
                      const status = String(row.status || "").toLowerCase();

                      return lines.map((wallet, index) => (
                        <tr
                          key={`${userId || user.uuid}-${wallet?.currency_id || wallet?.currency || index}`}
                          className={`rrl-row ${index > 0 ? "rrl-row--nested" : ""}`}
                          onClick={() => openDetail(userId)}
                        >
                          {index === 0 ? (
                            <>
                              <td rowSpan={span}>
                                <div className="rrl-user">
                                  <strong>{name}</strong>
                                  <span>{user.email || "—"}</span>
                                </div>
                              </td>
                              <td rowSpan={span} onClick={(e) => e.stopPropagation()}>
                                {user.uuid ? (
                                  <span className="rrl-copy">
                                    <code>{user.uuid}</code>
                                    <button
                                      type="button"
                                      className="rrl-copy-btn"
                                      title="Copy UUID"
                                      onClick={(e) => handleCopyUuid(e, user.uuid)}
                                    >
                                      <i className="fas fa-copy" />
                                    </button>
                                  </span>
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td rowSpan={span}>
                                <span className={`rrl-badge rrl-badge--${status || "muted"}`}>
                                  {status || "—"}
                                </span>
                              </td>
                              <td rowSpan={span}>{formatMoney(row.total_reward, ccy)}</td>
                              <td rowSpan={span} className="rrl-amt--paid">
                                {formatMoney(row.released_amount, ccy)}
                              </td>
                              <td rowSpan={span} className="rrl-amt--remain">
                                {formatMoney(row.remaining_amount, ccy)}
                              </td>
                              <td rowSpan={span}>
                                {row.last_released_round != null ? `Round ${row.last_released_round}` : "—"}
                              </td>
                              <td rowSpan={span}>
                                {row.next_round
                                  ? `Round ${row.next_round} · ${formatMoney(
                                      row.next_round_amount,
                                      row.next_round_currency || ccy
                                    )}`
                                  : "—"}
                              </td>
                            </>
                          ) : null}
                          <td>
                            {wallet
                              ? `${formatMoney(wallet.balance, wallet.currency)} · ${formatWalletType(
                                  wallet.wallet_type
                                )}`
                              : "—"}
                          </td>
                          {index === 0 ? (
                            <td rowSpan={span} onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                className="staking-stakes__view-btn"
                                onClick={() => openDetail(userId)}
                              >
                                <i className="fas fa-eye" /> View
                              </button>
                            </td>
                          ) : null}
                        </tr>
                      ));
                    })}
                  </tbody>
                </table>
              </div>
              <div className="rrl-pagination">
                <label className="staking-page-limit">
                  <span>Rows</span>
                  <select
                    value={pageLimit}
                    onChange={(e) => setPageLimit(Number(e.target.value))}
                    className="openorders-search-input staking-page-limit__select"
                  >
                    {LIST_LIMIT_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="cp-btn cp-btn--outline"
                  disabled={!pagination.has_prev || loading || page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span className="openorders-stat" style={{ margin: 0 }}>
                  Showing {pageStart}–{pageEnd}
                  {pagination.total != null ? ` of ${pagination.total}` : ""}
                </span>
                <button
                  type="button"
                  className="cp-btn cp-btn--outline"
                  disabled={!pagination.has_next || loading || page >= pagination.total_pages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </>
          ) : (
            <div className="rrl-empty">
              <i className="fas fa-inbox" />
              <p>No vesting users found</p>
              {hasFilters ? (
                <button type="button" className="cp-btn cp-btn--outline" onClick={handleReset}>
                  Clear filters
                </button>
              ) : null}
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default RoundReleaseLedgerPage;
